import { Component, ElementRef, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { PrintService } from '../../services/print.service';
import { normalizeTransactionGlLines, TransactionGlLine } from '../../models/transaction-gl.model';
import { GlTransactionDetailData, TransactionDetailModalComponent } from '../transaction-detail-modal/transaction-detail-modal.component';

@Component({
	selector: 'app-transaction-gl-table',
	templateUrl: './transaction-gl-table.component.html',
	styleUrls: ['./transaction-gl-table.component.scss'],
})
export class TransactionGlTableComponent implements OnChanges {
	@Input() lines: TransactionGlLine[] | null | undefined;
	/** Used for filenames, PDF heading, Excel; optional on-screen heading below parent chrome */
	@Input() exportTitle = '';
	@Input() showExportActions = true;
	/** When false, exportTitle is still used for exports but no extra screen heading is shown (e.g. dialog already has a title). */
	@Input() showScreenHeading = true;
	/** When set with documentTransno, the parent can refetch after line edits. */
	@Input() documentTypeId: string | number | null | undefined;
	@Input() documentTransno: string | number | null | undefined;

	@Output() lineSaved = new EventEmitter<void>();

	@ViewChild('pdfExportRoot', { static: false }) pdfExportRoot: ElementRef<HTMLElement> | undefined;

	normalized: TransactionGlLine[] = [];
	debitTotal = 0;
	creditTotal = 0;
	balanced = false;
	empty = false;
	exportingPdf = false;

	constructor(
		private globalSearchService: GlobalSearchService,
		private printService: PrintService,
		private dialog: MatDialog
	) {}

	ngOnChanges(): void {
		this.normalized = normalizeTransactionGlLines(this.lines);
		this.empty = this.normalized.length === 0;
		this.debitTotal = this.normalized.reduce((s, r) => s + (Number(r.debit) || 0), 0);
		this.creditTotal = this.normalized.reduce((s, r) => s + (Number(r.credit) || 0), 0);
		this.balanced = Math.abs(this.debitTotal - this.creditTotal) < 0.001;
	}

	lineCanEdit(line: TransactionGlLine): boolean {
		return line.counterindex != null && line.counterindex > 0;
	}

	openLineEdit(line: TransactionGlLine, ev: Event): void {
		ev.stopPropagation();
		if (!this.lineCanEdit(line)) {
			return;
		}
		const trandate =
			line.trandateRaw && line.trandateRaw.trim() !== '' ? line.trandateRaw : line.date;
		const tr = line.tagref;
		const detail: GlTransactionDetailData = {
			type: String(this.documentTypeId ?? ''),
			typeid: this.documentTypeId,
			number: String(this.documentTransno ?? ''),
			trandate,
			debit: line.debit,
			credit: line.credit,
			chequeno: '',
			narrative: line.description,
			balance: 0,
			tag: line.department || line.tagdescription || '',
			periodno: line.periodno,
			periodname: line.period,
			counterindex: line.counterindex,
			tagref:
				tr != null && !Number.isNaN(Number(tr)) && Number(tr) > 0
					? Number(tr)
					: null,
		};
		this.dialog
			.open(TransactionDetailModalComponent, {
				width: '1100px',
				maxWidth: '94vw',
				data: detail,
			})
			.afterClosed()
			.subscribe((result: { saved?: boolean } | undefined) => {
				if (result?.saved) {
					this.lineSaved.emit();
				}
			});
	}

	exportXlsx(): void {
		if (this.empty) {
			return;
		}
		const rows = this.normalized.map((line) => ({
			Period: line.period,
			Date: line.date,
			'Account code': line.accountcode || '',
			'GL account': line.account,
			Description: line.description,
			Department: line.department || '',
			Debits: line.debit > 0 ? line.debit : '',
			Credits: line.credit > 0 ? line.credit : '',
			Posted: line.posted,
		}));
		rows.push({
			Period: '',
			Date: '',
			'Account code': '',
			'GL account': '',
			Description: 'Totals',
			Department: '',
			Debits: this.debitTotal,
			Credits: this.creditTotal,
			Posted: '',
		});
		this.globalSearchService.exportJsonToExcel(rows, this.fileBase());
	}

	exportPdf(): void {
		if (this.empty || !this.pdfExportRoot?.nativeElement) {
			return;
		}
		this.exportingPdf = true;
		const filename = `${this.fileBase()}.pdf`;
		const titleBlock = this.exportTitle
			? `<h2 style="font-size:15px;margin:0 0 10px;font-family:sans-serif">${this.escapeHtml(this.exportTitle)}</h2>`
			: '';
		const encoded = this.globalSearchService.base64encode(titleBlock + this.pdfExportRoot.nativeElement.innerHTML);
		this.printService.pdf({ content: encoded, filename }).subscribe({
			next: (result: any) => {
				this.globalSearchService.downloadPdf(result.content, filename);
				this.exportingPdf = false;
			},
			error: () => {
				this.exportingPdf = false;
			},
		});
	}

	private fileBase(): string {
		const raw = (this.exportTitle || 'GL_transactions')
			.replace(/[^a-zA-Z0-9]+/g, '_')
			.replace(/^_|_$/g, '');
		return (raw || 'GL_transactions') + '_' + new Date().toISOString().slice(0, 10);
	}

	private escapeHtml(text: string): string {
		return String(text)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}
}
